import { Button, Chip, Tab, Tabs, useDisclosure } from "@heroui/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaArrowRight, FaPlus } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import {
  Ticket,
  TicketPriority,
  TicketStatus,
  TicketType,
} from "@/api/tickets/tickets.ts";
import { CreateTicketModal } from "@/app/components/modals/tickets/createTicketModal";
import { EditTicketModal } from "@/app/components/modals/tickets/editTicketModal";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { useTour } from "@/hooks/useTour";
import useErrorHandling from "@/hooks/useErrorHandling";

export function TicketsPage() {
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [rawTickets, setRawTickets] = useState<Ticket[] | null>(null);
  const [activeTab, setActiveTab] = useState<string>("user_to_landlord");
  const [activeStatus, setActiveStatus] = useState<string>("all");
  const [activePriority, setActivePriority] = useState<string>("all");
  const navigate = useNavigate();

  const handleError = useErrorHandling();

  useTour(5);

  const createTicketModalDisclosure = useDisclosure();
  const editTicketModalDisclosure = useDisclosure();
  const fullscreenDisclosure = useDisclosure();

  useEffect(() => {
    setTickets(
      rawTickets?.filter((ticket) => {
        if (activeStatus === "all") return true;
        if (activeStatus === "open") return ticket.status === "open";
        if (activeStatus === "in_progress")
          return ticket.status === "in_progress";
        if (activeStatus === "closed") return ticket.status === "closed";
        return true;
      }) || [],
    );
  }, [activeStatus, rawTickets]);

  useEffect(() => {
    api.tickets
      .list(activeTab as TicketType)
      .then((response) => {
        setRawTickets(response.data);
      })
      .catch((err) => {
        toast.error("Произошла ошибка при загрузке тикетов!");
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [
    activeTab,
    activePriority,
    createTicketModalDisclosure.isOpen,
    editTicketModalDisclosure.isOpen,
    handleError,
  ]);

  return (
    <div className="flex flex-row flex-wrap gap-4 w-full">
      <CreateTicketModal disclosure={createTicketModalDisclosure} />
      <EditTicketModal
        disclosure={editTicketModalDisclosure}
        ticket={null as Ticket | null}
      />
      <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
        <div className="flex flex-row justify-between w-full items-center mb-4">
          <div className="flex flex-row gap-4 items-center">
            <span className="font-medium" id="tickets-title">
              Тикеты
            </span>
          </div>
          <div className="flex flex-row gap-4 items-center">
            <Button
              id="create-ticket-button"
              startContent={<FaPlus />}
              color="primary"
              variant="solid"
              className="max-w-fit"
              onPress={() => createTicketModalDisclosure.onOpen()}
            >
              Новый тикет
            </Button>
          </div>
        </div>
        <div className="flex flex-row flex-wrap gap-4 mb-4">
          <Tabs
            color="primary"
            variant="bordered"
            selectedKey={activeTab}
            onSelectionChange={(key) => setActiveTab(key as string)}
          >
            <Tab key="user_to_landlord" title="Бизнес" />
            <Tab key="to_platform" title="Платформа" />
          </Tabs>

          <Tabs
            color="primary"
            variant="bordered"
            selectedKey={activeStatus}
            onSelectionChange={(key) => setActiveStatus(key as string)}
          >
            <Tab key="all" title="Все статусы" />
            <Tab key="open" title="Открытые" />
            <Tab key="in_progress" title="В работе" />
            <Tab key="closed" title="Закрытые" />
          </Tabs>

          <Tabs
            color="primary"
            variant="bordered"
            selectedKey={activePriority}
            onSelectionChange={(key) => setActivePriority(key as string)}
          >
            <Tab key="all" title="Все приоритеты" />
            <Tab key="low" title="Низкий" />
            <Tab key="medium" title="Средний" />
            <Tab key="high" title="Высокий" />
            <Tab key="urgent" title="Срочный" />
          </Tabs>
        </div>
        <div className="flex flex-col gap-2 mb-2">
          <TableBuilder
            fullscreenDisclosure={fullscreenDisclosure}
            removeWrapper
            displayOptions={false}
            columns={[
              {
                key: "subject",
                label: "Тема обращения",
                type: ColumnType.String,
                sortable: true,
              },
              {
                key: "priority",
                label: "Приоритет",
                type: ColumnType.Custom,
                sortable: true,
                render(value: TicketPriority) {
                  return (
                    <Chip
                      color={
                        value === "low"
                          ? "default"
                          : value === "medium"
                            ? "warning"
                            : value === "high"
                              ? "danger"
                              : value === "urgent"
                                ? "danger"
                                : "default"
                      }
                      variant="bordered"
                    >
                      {value === "low"
                        ? "Низкий"
                        : value === "medium"
                          ? "Средний"
                          : value === "high"
                            ? "Высокий"
                            : "Срочный"}
                    </Chip>
                  );
                },
              },
              {
                key: "status",
                label: "Статус",
                type: ColumnType.Custom,
                sortable: true,
                render(value: TicketStatus) {
                  return (
                    <Chip
                      color={
                        value === "open"
                          ? "default"
                          : value === "in_progress"
                            ? "success"
                            : "default"
                      }
                      variant="bordered"
                    >
                      {value === "open"
                        ? "Открыт"
                        : value === "in_progress"
                          ? "В работе"
                          : "Закрыт"}
                    </Chip>
                  );
                },
              },
              {
                key: "category",
                label: "Категория",
                type: ColumnType.Category,
                sortable: true,
              },
              {
                key: "created_at",
                label: "Создан",
                type: ColumnType.DateTime,
                sortable: true,
              },
              {
                key: "actions",
                label: "Действия",
                type: ColumnType.Actions,
                actions: [
                  {
                    icon: <FaArrowRight />,
                    onClick: (_value, row) => {
                      navigate(`/dashboard/tickets/${row.type}/${row.id}`);
                    },
                  },
                ],
              },
            ]}
            data={tickets || []}
            rowsPerPage={10}
            defaultSortDescriptor={{
              column: "priority",
              direction: "ascending",
            }}
          />
        </div>
      </div>
    </div>
  );
}
